import { useState } from "react";
import { mediaUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star, Upload, Tv, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/store/useStore";
import { cn } from "@/lib/utils";
import { MediaPicker } from "@/components/MediaPicker";

export function BrandsPage() {
  const { brands, addBrand, updateBrand, deleteBrand, setDefaultBrand } = useStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    logo: "",
    showWatchOnText: true,
  });

  const resetForm = () => {
    setFormData({ name: "", logo: "", showWatchOnText: true });
    setEditingBrand(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, logo: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Brand name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, formData);
        toast.success("Brand updated");
      } else {
        await addBrand(formData);
        toast.success("Brand added");
      }
      resetForm();
      setIsDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save brand");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      logo: brand.logo || "",
      showWatchOnText: brand.showWatchOnText !== false,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (brand) => {
    if (brands.length <= 1) {
      toast.error("You need at least one brand. Edit the existing one instead.");
      return;
    }
    if (!window.confirm(`Delete "${brand.name}"?`)) return;
    try {
      await deleteBrand(brand.id);
      toast.success("Brand deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete brand");
    }
  };

  const handleSetDefault = async (brand) => {
    if (brand.isDefault) return;
    try {
      await setDefaultBrand(brand.id);
      toast.success(`${brand.name} is now the default brand`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set default");
    }
  };

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-foreground">Branding</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage the watermark logos shown at the bottom of every thumbnail.
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button variant="premium" className="gap-2" data-testid="add-brand-button" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="w-4 h-4" />
              Add Brand
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingBrand ? "Edit Brand" : "Add Brand"}</DialogTitle>
              <DialogDescription>
                Upload a PNG, JPG, or SVG logo. Set this as default to use it on all new exports.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="brand-name" className="text-xs">Name</Label>
                <Input
                  id="brand-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., New Logical Sports"
                  data-testid="brand-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Logo</Label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className="border-2 border-dashed border-border rounded-lg p-3 text-center hover:border-primary/50 transition-colors">
                      <Upload className="w-4 h-4 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">
                        {formData.logo ? "Replace logo" : "PNG, JPG, or SVG"}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,.svg"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="brand-logo-input"
                    />
                  </label>
                  {formData.logo && (
                    <div className="w-16 h-16 rounded-lg bg-secondary/50 border border-border flex items-center justify-center p-2 shrink-0">
                      <img src={mediaUrl(formData.logo)} alt="preview" className="max-w-full max-h-full object-contain"  loading="lazy" decoding="async"/>
                    </div>
                  )}
                </div>
                {formData.logo && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setFormData({ ...formData, logo: "" })}
                  >
                    Remove logo (use text fallback)
                  </Button>
                )}
                <Button
                  type="button" variant="outline" size="sm" className="h-7 text-xs gap-1.5"
                  onClick={() => setIsMediaPickerOpen(true)}
                  data-testid="brand-pick-from-media"
                >
                  <FolderOpen className="w-3 h-3" />
                  Pick from Media Library
                </Button>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="show-watch-on" className="text-xs">Show "WATCH ON" above logo</Label>
                  <p className="text-[11px] text-muted-foreground">Turn off if your logo already includes a tagline.</p>
                </div>
                <Switch
                  id="show-watch-on"
                  checked={formData.showWatchOnText}
                  onCheckedChange={(v) => setFormData({ ...formData, showWatchOnText: v })}
                  data-testid="brand-show-watch-on-switch"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" size="sm" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={submitting} data-testid="brand-submit-button">
                  {submitting ? "Saving..." : editingBrand ? "Save Changes" : "Add Brand"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {brands.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <Tv className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No brands yet. Add one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand) => (
            <Card
              key={brand.id}
              className={cn(
                "glass-card group hover:border-primary/50 transition-colors",
                brand.isDefault && "ring-2 ring-primary/40"
              )}
              data-testid={`brand-card-${brand.id}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="aspect-video rounded-lg bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4 border border-border">
                  {brand.logo ? (
                    <div className="text-center">
                      {brand.showWatchOnText && (
                        <p className="text-[8px] tracking-widest text-white/80 uppercase mb-1 font-bold">Watch on</p>
                      )}
                      <img src={mediaUrl(brand.logo)} alt={brand.name} className="max-h-12 max-w-full object-contain mx-auto"  loading="lazy" decoding="async"/>
                    </div>
                  ) : (
                    <p className="text-white text-center text-xs uppercase font-bold tracking-widest">
                      Watch on
                      <br />
                      <span className="text-sm">{brand.name}</span>
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm truncate" title={brand.name}>
                      {brand.name}
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                      {brand.logo ? "Image logo" : "Text only"}
                      {brand.isDefault && " · Default"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className={cn("h-7 w-7", brand.isDefault && "text-primary")}
                      onClick={() => handleSetDefault(brand)}
                      title={brand.isDefault ? "Default brand" : "Set as default"}
                      data-testid={`brand-default-${brand.id}`}
                    >
                      <Star className={cn("w-3.5 h-3.5", brand.isDefault && "fill-current")} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="h-7 w-7"
                      onClick={() => handleEdit(brand)}
                      data-testid={`brand-edit-${brand.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(brand)}
                      data-testid={`brand-delete-${brand.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <MediaPicker
        open={isMediaPickerOpen}
        onOpenChange={setIsMediaPickerOpen}
        onSelect={(item) => setFormData((prev) => ({ ...prev, logo: item.url }))}
        title="Pick a brand logo"
      />
    </div>
  );
}
